// src/frames/Appeals.tsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from "../App";

type AppealStatus = 'open' | 'accepted' | 'rejected' | 'info_requested' | 'outstanding';

interface RequestMessage {
    id: string;
    fromStaff: boolean;
    message: string;
    createdAt: string;
}

interface BlacklistTemplate {
    id: string;
    name: string;
    message: string;
    createdAt: string;
    updatedAt: string;
}

interface BlacklistEntry {
    id: string;
    discordId: string;
    addedById: string;
    addedByName: string;
    message: string;
    timeframe: string;
    templateId: string | null;
    createdAt: string;
    expiresAt: string | null;
}

interface Appeal {
    id: string;
    discordId: string;
    username: string;
    banDate: string | null;
    banReason: string;
    understandingObjection: string;
    ifUnbanned: string;
    additionalComments: string;
    status: AppealStatus;
    submittedAt: string;
    updatedAt: string;
    resolvedById: string | null;
    resolvedByName: string | null;
    resolutionComments: string;
    requestMessages?: RequestMessage[];
}

interface PanelUser {
    id: string;
    username: string;
    discriminator: string;
    avatar: string;
}

interface AppealsProps {
    user: PanelUser;
}

type SortField = 'submittedAt' | 'username';

const STATUS_LABELS: Record<AppealStatus, string> = {
    open: 'Open',
    accepted: 'Accepted',
    rejected: 'Rejected',
    info_requested: 'Info Requested',
    outstanding: 'Outstanding'
};

const Appeals: React.FC<AppealsProps> = ({ user }) => {
    const [resolutionTarget, setResolutionTarget] = useState<Appeal | null>(null);
    const [resolutionStatus, setResolutionStatus] = useState<AppealStatus | null>(null);
    const [resolutionComments, setResolutionComments] = useState<string>('');
    const [resolutionError, setResolutionError] = useState<string | null>(null);
    const [isOverride, setIsOverride] = useState<boolean>(false);
    const [overrideNewStatus, setOverrideNewStatus] = useState<AppealStatus>('open');

    const [appeals, setAppeals] = useState<Appeal[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [filterUsername, setFilterUsername] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<'all' | AppealStatus>('outstanding');

    const [sortField, setSortField] = useState<SortField>('submittedAt');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    const [selectedAppeal, setSelectedAppeal] = useState<Appeal | null>(null);

    // Request-info state
    const [requestTarget, setRequestTarget] = useState<Appeal | null>(null);
    const [requestQuestion, setRequestQuestion] = useState<string>('');
    const [requestError, setRequestError] = useState<string | null>(null);
    const [requestLoading, setRequestLoading] = useState<boolean>(false);
    const [requestHistoryExpanded, setRequestHistoryExpanded] = useState<boolean>(false);

    // ── Tabs ───────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<'appeals' | 'blacklist'>('appeals');
    const [blacklistSubTab, setBlacklistSubTab] = useState<'users' | 'templates'>('users');

    // ── Blacklist state ────────────────────────────────────────────────
    const [blacklistEntries, setBlacklistEntries] = useState<BlacklistEntry[]>([]);
    const [blacklistTemplates, setBlacklistTemplates] = useState<BlacklistTemplate[]>([]);
    const [blacklistLoading, setBlacklistLoading] = useState(false);
    const [blShowExpired, setBlShowExpired] = useState(false);
    const [blacklistError, setBlacklistError] = useState<string | null>(null);

    // Add-entry form
    const [blDiscordId, setBlDiscordId] = useState('');
    const [blSelectedTemplate, setBlSelectedTemplate] = useState('');
    const [blAdhocMessage, setBlAdhocMessage] = useState('');
    const [blUseTemplate, setBlUseTemplate] = useState(true);
    const [blSubmitting, setBlSubmitting] = useState(false);
    const [blFormError, setBlFormError] = useState<string | null>(null);

    // Duration input mode for the add form
    const [blDurationMode, setBlDurationMode] = useState<'duration' | 'datetime' | 'permanent'>('duration');
    const [blDurationStr, setBlDurationStr] = useState('');   // e.g. "1m14d"
    const [blDatetime, setBlDatetime] = useState('');          // datetime-local value

    // Template editor
    const [tmplModalOpen, setTmplModalOpen] = useState(false);
    const [tmplEditTarget, setTmplEditTarget] = useState<BlacklistTemplate | null>(null);
    const [tmplName, setTmplName] = useState('');
    const [tmplMessage, setTmplMessage] = useState('');
    const [tmplError, setTmplError] = useState<string | null>(null);
    const [tmplSubmitting, setTmplSubmitting] = useState(false);

    const loadAppeals = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(`${API_URL}/api/appeals`);
            const raw = response.data;
            const appealsData = Array.isArray(raw) ? raw : raw?.appeals;
            setAppeals(Array.isArray(appealsData) ? appealsData : []);
        } catch (err) {
            console.error(err);
            setError('Failed to load appeals');
            setAppeals([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAppealMessages = async (appealId: string) => {
        try {
            const res = await axios.get(`${API_URL}/api/appeals/${appealId}/messages`);
            const messages: RequestMessage[] = res.data.messages ?? [];
            setAppeals(prev => prev.map(a =>
                a.id === appealId ? { ...a, requestMessages: messages } : a
            ));
            if (selectedAppeal?.id === appealId) {
                setSelectedAppeal(prev => prev ? { ...prev, requestMessages: messages } : null);
            }
        } catch (err) {
            console.error('Failed to load messages for appeal', appealId, err);
        }
    };

    useEffect(() => {
        loadAppeals();
    }, []);

    useEffect(() => {
        if (activeTab === 'blacklist') {
            loadBlacklistData();
        }
    }, [activeTab]);

    const loadBlacklistData = async () => {
        try {
            setBlacklistLoading(true);
            setBlacklistError(null);
            const [entriesRes, templatesRes] = await Promise.all([
                axios.get(`${API_URL}/api/appeals/blacklist`),
                axios.get(`${API_URL}/api/appeals/blacklist/templates`),
            ]);
            setBlacklistEntries(entriesRes.data.entries ?? []);
            setBlacklistTemplates(templatesRes.data.templates ?? []);
        } catch (err) {
            console.error(err);
            setBlacklistError('Failed to load blacklist data.');
        } finally {
            setBlacklistLoading(false);
        }
    };

    const handleAddBlacklist = async () => {
        setBlFormError(null);
        if (!blDiscordId.trim()) { setBlFormError('Discord ID is required.'); return; }
        if (!blParsed) { setBlFormError('A valid duration, date/time, or permanent selection is required.'); return; }
        if (blUseTemplate && !blSelectedTemplate) { setBlFormError('Please select a template.'); return; }
        if (!blUseTemplate && !blAdhocMessage.trim()) { setBlFormError('Message is required.'); return; }
        if (!blUseTemplate && !blAdhocMessage.includes('{timeframe}')) {
            setBlFormError('Ad-hoc message must contain {timeframe}.');
            return;
        }
        try {
            setBlSubmitting(true);
            await axios.post(`${API_URL}/api/appeals/blacklist`, {
                discordId: blDiscordId.trim(),
                timeframe: blParsed.label,
                expiresAt: blParsed.expiresAt ? blParsed.expiresAt.toISOString() : null,
                templateId: blUseTemplate ? blSelectedTemplate : undefined,
                message: blUseTemplate ? undefined : blAdhocMessage.trim(),
            });
            setBlDiscordId('');
            setBlDurationStr('');
            setBlDatetime('');
            setBlSelectedTemplate('');
            setBlAdhocMessage('');
            await loadBlacklistData();
        } catch (err: any) {
            setBlFormError(err.response?.data?.error || 'Failed to add blacklist entry.');
        } finally {
            setBlSubmitting(false);
        }
    };

    const handleRemoveBlacklist = async (id: string) => {
        try {
            await axios.delete(`${API_URL}/api/appeals/blacklist/${id}`);
            await loadBlacklistData();
        } catch (err) {
            setBlacklistError('Failed to remove blacklist entry.');
        }
    };

    const openTmplModal = (tmpl: BlacklistTemplate | null) => {
        setTmplEditTarget(tmpl);
        setTmplName(tmpl?.name ?? '');
        setTmplMessage(tmpl?.message ?? '');
        setTmplError(null);
        setTmplModalOpen(true);
    };

    const closeTmplModal = () => {
        setTmplModalOpen(false);
        setTmplEditTarget(null);
        setTmplName('');
        setTmplMessage('');
        setTmplError(null);
    };

    const handleSaveTemplate = async () => {
        setTmplError(null);
        if (!tmplName.trim()) { setTmplError('Name is required.'); return; }
        if (!tmplMessage.trim()) { setTmplError('Message is required.'); return; }
        if (!tmplMessage.includes('{timeframe}')) { setTmplError('Message must contain {timeframe}.'); return; }
        try {
            setTmplSubmitting(true);
            if (tmplEditTarget) {
                await axios.patch(`${API_URL}/api/appeals/blacklist/templates/${tmplEditTarget.id}`, {
                    name: tmplName.trim(), message: tmplMessage.trim()
                });
            } else {
                await axios.post(`${API_URL}/api/appeals/blacklist/templates`, {
                    name: tmplName.trim(), message: tmplMessage.trim()
                });
            }
            closeTmplModal();
            await loadBlacklistData();
        } catch (err: any) {
            setTmplError(err.response?.data?.error || 'Failed to save template.');
        } finally {
            setTmplSubmitting(false);
        }
    };

    const handleDeleteTemplate = async (id: string) => {
        try {
            await axios.delete(`${API_URL}/api/appeals/blacklist/templates/${id}`);
            await loadBlacklistData();
        } catch (err) {
            setBlacklistError('Failed to delete template.');
        }
    };

    useEffect(() => {
        if (selectedAppeal) {
            loadAppealMessages(selectedAppeal.id);
            setRequestHistoryExpanded(false);
        }
    }, [selectedAppeal?.id]);

    const handleStatusChange = async (
        appeal: Appeal,
        status: AppealStatus,
        resolutionComments: string
    ) => {
        try {
            setError(null);

            const response = await axios.patch(
                `${API_URL}/api/appeals/${appeal.id}/status`,
                {
                    status,
                    moderatorId: user.id,
                    moderatorName: `${user.username}`,
                    resolutionComments,
                }
            );

            const updated: Appeal = response.data.appeal || { ...appeal, status };

            setAppeals(prev => prev.map(a => a.id === updated.id ? updated : a));
            if (selectedAppeal?.id === updated.id) setSelectedAppeal(updated);

            setResolutionTarget(null);
            setResolutionStatus(null);
            setResolutionComments('');
            setResolutionError(null);
        } catch (err: any) {
            console.error(err);
            setError('Failed to update appeal status');
            if (err.response?.data?.error) setResolutionError(err.response.data.error);
        }
    };

    const confirmResolution = () => {
        if (!resolutionTarget) return;
        const effectiveStatus = isOverride ? overrideNewStatus : resolutionStatus;
        if (!effectiveStatus) return;
        if (effectiveStatus !== 'open' && effectiveStatus !== 'info_requested' && !resolutionComments.trim()) {
            setResolutionError('Please enter resolution comments before continuing.');
            return;
        }
        handleStatusChange(resolutionTarget, effectiveStatus, resolutionComments.trim());
    };

    const closeResolutionModal = () => {
        setResolutionTarget(null);
        setResolutionStatus(null);
        setResolutionComments('');
        setResolutionError(null);
        setIsOverride(false);
    };

    const openResolutionModal = (appeal: Appeal, status: AppealStatus) => {
        setResolutionTarget(appeal);
        setResolutionStatus(status);
        setResolutionComments(appeal.resolutionComments || '');
        setResolutionError(null);
        setIsOverride(false);
    };

    const openOverrideModal = (appeal: Appeal) => {
        const defaultNext: AppealStatus =
            appeal.status === 'accepted' ? 'rejected' :
                appeal.status === 'rejected' ? 'open' : 'accepted';
        setResolutionTarget(appeal);
        setResolutionStatus(defaultNext);
        setOverrideNewStatus(defaultNext);
        setResolutionComments('');
        setResolutionError(null);
        setIsOverride(true);
    };

    const openRequestModal = async (appeal: Appeal) => {
        setRequestQuestion('');
        setRequestError(null);

        // Always fetch latest messages
        try {
            const res = await axios.get(`${API_URL}/api/appeals/${appeal.id}/messages`);
            const messages: RequestMessage[] = res.data.messages ?? [];

            const updatedAppeal = {
                ...appeal,
                requestMessages: messages
            };

            // Sync everything
            setAppeals(prev => prev.map(a =>
                a.id === appeal.id ? updatedAppeal : a
            ));

            setSelectedAppeal(updatedAppeal);
            setRequestTarget(updatedAppeal);
        } catch (err) {
            console.error('Failed to load messages for request modal', err);
            setRequestTarget(appeal); // fallback
        }
    };

    const closeRequestModal = () => {
        setRequestTarget(null);
        setRequestQuestion('');
        setRequestError(null);
    };

    const submitInfoRequest = async () => {
        if (!requestTarget) return;
        if (!requestQuestion.trim()) {
            setRequestError('Please enter a question before sending.');
            return;
        }
        try {
            setRequestLoading(true);
            setRequestError(null);

            const response = await axios.post(
                `${API_URL}/api/appeals/${requestTarget.id}/info-request`,
                { question: requestQuestion.trim() }
            );

            const updated: Appeal = response.data.appeal || { ...requestTarget, status: 'info_requested' as AppealStatus };
            setAppeals(prev => prev.map(a => a.id === updated.id ? updated : a));
            if (selectedAppeal?.id === updated.id) setSelectedAppeal(updated);

            closeRequestModal();
        } catch (err: any) {
            console.error(err);
            setRequestError(err.response?.data?.error || 'Failed to send info request.');
        } finally {
            setRequestLoading(false);
        }
    };

    const handleSortClick = (field: SortField) => {
        setSortField(currentField => {
            if (currentField === field) {
                setSortDirection(dir => dir === 'asc' ? 'desc' : 'asc');
                return currentField;
            }
            setSortDirection('desc');
            return field;
        });
    };

    const visibleAppeals = useMemo(() => {
        let list = [...appeals];

        if (filterUsername.trim()) {
            const term = filterUsername.toLowerCase();
            list = list.filter(a => a.username.toLowerCase().includes(term));
        }

        if (filterStatus !== 'all') {
            if (filterStatus === 'outstanding') {
                list = list.filter(a =>
                    a.status === 'open' || a.status === 'info_requested'
                );
            } else {
                list = list.filter(a => a.status === filterStatus);
            }
        }

        list.sort((a, b) => {
            if (sortField === 'username') {
                const aName = a.username.toLowerCase();
                const bName = b.username.toLowerCase();
                if (aName < bName) return sortDirection === 'asc' ? -1 : 1;
                if (aName > bName) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            } else {
                const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
                const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
                return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
            }
        });

        return list;
    }, [appeals, filterUsername, filterStatus, sortField, sortDirection]);

    const formatDateTime = (value: string | null): string => {
        if (!value) return 'N/A';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString('en-GB');
    };

    const formatDate = (value: string | null): string => {
        if (!value) return 'N/A';
        return new Date(value).toDateString();
    };

    const sortLabel = (field: SortField) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const renderRequestThread = (appeal: Appeal) => {
        const msgs = appeal.requestMessages ?? [];
        if (msgs.length === 0) return null;

        const lastStaff = [...msgs].reverse().find(m => m.fromStaff);
        const lastUser = [...msgs].reverse().find(m => !m.fromStaff);
        const olderMessages = msgs.slice(0, msgs.length - (lastStaff ? 1 : 0) - (lastUser && lastUser !== lastStaff ? 1 : 0));

        const pinnedMessages: RequestMessage[] = [];
        if (lastStaff) pinnedMessages.push(lastStaff);
        if (lastUser && lastUser.id !== lastStaff?.id) pinnedMessages.push(lastUser);
        pinnedMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        const hiddenCount = msgs.length - pinnedMessages.length;

        return (
            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem', opacity: 0.9 }}>
                    Info Request Thread
                </h3>

                {hiddenCount > 0 && (
                    <button
                        className="button is-small"
                        style={{ marginBottom: '0.5rem', opacity: 0.7, fontSize: '0.8rem' }}
                        onClick={() => setRequestHistoryExpanded(e => !e)}
                    >
                        {requestHistoryExpanded
                            ? `▲ Hide older messages`
                            : `▼ Show ${hiddenCount} older message${hiddenCount !== 1 ? 's' : ''}`}
                    </button>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {requestHistoryExpanded && msgs.slice(0, msgs.length - pinnedMessages.length).map(msg => (
                        <MessageBubble key={msg.id} msg={msg} username={appeal.username} dimmed />
                    ))}
                    {pinnedMessages.map(msg => (
                        <MessageBubble key={msg.id} msg={msg} username={appeal.username} />
                    ))}
                </div>
            </div>
        );
    };
    // ── Duration parser (mirrors server-side logic) ─────────────────
    const parseDurationClient = (input: string): { expiresAt: Date; label: string } | null => {
        const re = /(\d+)\s*(y(?:ears?)?|mo(?:nths?)?|m(?:onths?)?|w(?:eeks?)?|d(?:ays?)?|h(?:ours?)?)/gi;
        let match: RegExpExecArray | null;
        const parts: { value: number; unit: string }[] = [];
        while ((match = re.exec(input)) !== null) {
            parts.push({ value: parseInt(match[1], 10), unit: match[2].toLowerCase() });
        }
        if (parts.length === 0) return null;

        let years = 0, months = 0, weeks = 0, days = 0, hours = 0;
        for (const p of parts) {
            if (p.unit.startsWith('y')) years += p.value;
            else if (p.unit.startsWith('m')) months += p.value;
            else if (p.unit.startsWith('w')) weeks += p.value;
            else if (p.unit.startsWith('d')) days += p.value;
            else if (p.unit.startsWith('h')) hours += p.value;
        }

        const end = new Date();
        end.setFullYear(end.getFullYear() + years);
        end.setMonth(end.getMonth() + months);
        end.setDate(end.getDate() + weeks * 7 + days);
        end.setHours(end.getHours() + hours);

        const labelParts: string[] = [];
        if (years) labelParts.push(`${years} year${years !== 1 ? 's' : ''}`);
        if (months) labelParts.push(`${months} month${months !== 1 ? 's' : ''}`);
        if (weeks) labelParts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
        if (days) labelParts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours) labelParts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);

        const dateStr = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
        return { expiresAt: end, label: `until ${dateStr} UTC` };
    };

    // Derive computed expiry + label from whichever input mode is active
    const blParsed: { expiresAt: Date | null; label: string } | null =
        blDurationMode === 'permanent'
            ? { expiresAt: null, label: 'permanently' }
            : blDurationMode === 'duration'
                ? (blDurationStr.trim() ? parseDurationClient(blDurationStr.trim()) : null)
                : (blDatetime
                    ? { expiresAt: new Date(blDatetime), label: `until ${new Date(blDatetime).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC` }
                    : null);

    const renderBlacklistTab = () => (
        <div>
            {blacklistError && (
                <div className="error-message" style={{ marginBottom: '1rem' }}>{blacklistError}</div>
            )}

            {/* ── Blacklist sub-tabs ────────────────────────────────── */}
            <div className="sub-tabs">
                <button
                    className={`sub-tab${blacklistSubTab === 'users' ? ' sub-tab-active' : ''}`}
                    onClick={() => setBlacklistSubTab('users')}
                >
                    User Management
                </button>
                <button
                    className={`sub-tab${blacklistSubTab === 'templates' ? ' sub-tab-active' : ''}`}
                    onClick={() => setBlacklistSubTab('templates')}
                >
                    Message Templates
                </button>
            </div>

            {/* ── User Management sub-tab ───────────────────────────── */}
            {blacklistSubTab === 'users' && (
                <div>
                    {/* Add entry form */}
                    <div className="section" style={{ marginBottom: '1.5rem' }}>
                        <h3>Add User to Blacklist</h3>
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label className="form-label">Discord ID <span style={{ color: 'var(--danger)' }}>*</span></label>
                            <input
                                type="text"
                                placeholder="e.g. 123456789012345678"
                                value={blDiscordId}
                                onChange={e => setBlDiscordId(e.target.value)}
                            />
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                            <label className="form-label">Duration <span style={{ color: 'var(--danger)' }}>*</span></label>
                            <div className="blacklist-source-toggle" style={{ marginBottom: '0.5rem' }}>
                                <button
                                    className={`button is-small ${blDurationMode === 'duration' ? 'is-primary' : ''}`}
                                    onClick={() => setBlDurationMode('duration')}
                                >
                                    Duration
                                </button>
                                <button
                                    className={`button is-small ${blDurationMode === 'datetime' ? 'is-primary' : ''}`}
                                    onClick={() => setBlDurationMode('datetime')}
                                >
                                    Date / Time
                                </button>
                                <button
                                    className={`button is-small ${blDurationMode === 'permanent' ? 'is-danger' : ''}`}
                                    onClick={() => setBlDurationMode('permanent')}
                                >
                                    Permanent
                                </button>
                            </div>
                            {blDurationMode === 'duration' && (
                                <input
                                    type="text"
                                    placeholder="e.g. 1m, 7d, 2y3m, 1m14d — y/m/w/d/h"
                                    value={blDurationStr}
                                    onChange={e => setBlDurationStr(e.target.value)}
                                />
                            )}
                            {blDurationMode === 'datetime' && (
                                <input
                                    type="datetime-local"
                                    value={blDatetime}
                                    onChange={e => setBlDatetime(e.target.value)}
                                />
                            )}
                            {blDurationMode === 'permanent' && (
                                <p className="blacklist-permanent-warning">
                                    This user will be permanently blocked from submitting appeals with no expiry date.
                                </p>
                            )}
                            {blParsed && blDurationMode !== 'permanent' && (
                                <p className="blacklist-duration-preview">
                                    Expires: <strong>{blParsed.label.replace(/^until /, '')}</strong>
                                </p>
                            )}
                            {blDurationMode === 'duration' && blDurationStr && !blParsed && (
                                <p className="help is-danger" style={{ marginTop: '0.25rem', fontSize: '0.8rem' }}>
                                    Invalid format. Try: <code>7d</code>, <code>1m</code>, <code>2y3m</code>, <code>1m14d</code>
                                </p>
                            )}
                        </div>

                        <div className="blacklist-source-toggle">
                            <button
                                className={`button is-small ${blUseTemplate ? 'is-primary' : ''}`}
                                onClick={() => setBlUseTemplate(true)}
                            >
                                Use Template
                            </button>
                            <button
                                className={`button is-small ${!blUseTemplate ? 'is-primary' : ''}`}
                                onClick={() => setBlUseTemplate(false)}
                            >
                                Ad-hoc Message
                            </button>
                        </div>

                        {blUseTemplate ? (
                            <div style={{ marginTop: '0.75rem' }}>
                                <label className="form-label">Template <span style={{ color: 'var(--danger)' }}>*</span></label>
                                <select value={blSelectedTemplate} onChange={e => setBlSelectedTemplate(e.target.value)}>
                                    <option value="">— Select a template —</option>
                                    {blacklistTemplates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {blSelectedTemplate && (() => {
                                    const tmpl = blacklistTemplates.find(t => t.id === blSelectedTemplate);
                                    return tmpl ? (
                                        <p className="blacklist-preview">
                                            <strong>Preview:</strong> {tmpl.message.replace('{timeframe}', blParsed?.label || '{timeframe}')}
                                        </p>
                                    ) : null;
                                })()}
                            </div>
                        ) : (
                            <div style={{ marginTop: '0.75rem' }}>
                                <label className="form-label">
                                    Message <span style={{ color: 'var(--danger)' }}>*</span>
                                    <span className="blacklist-tag-hint"> — must include <code>{'{timeframe}'}</code></span>
                                </label>
                                <textarea
                                    placeholder={'e.g. You are currently unable to submit an appeal until {timeframe}.'}
                                    value={blAdhocMessage}
                                    onChange={e => setBlAdhocMessage(e.target.value)}
                                    rows={3}
                                />
                                {blAdhocMessage && (
                                    <p className="blacklist-preview">
                                        <strong>Preview:</strong> {blAdhocMessage.replace('{timeframe}', blParsed?.label ?? '{timeframe}')}
                                    </p>
                                )}
                            </div>
                        )}

                        {blFormError && <p className="help is-danger" style={{ marginTop: '0.5rem' }}>{blFormError}</p>}

                        <button
                            className="button is-danger"
                            style={{ marginTop: '0.75rem' }}
                            onClick={handleAddBlacklist}
                            disabled={blSubmitting}
                        >
                            {blSubmitting ? 'Adding…' : 'Add to Blacklist'}
                        </button>
                    </div>

                    {/* Active entries table */}
                    <div className="section">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <h3 style={{ margin: 0 }}>
                                Blacklisted Users ({blacklistEntries.filter(e => blShowExpired || !e.expiresAt || new Date(e.expiresAt) > new Date()).length})
                            </h3>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', opacity: 0.75, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={blShowExpired}
                                    onChange={e => setBlShowExpired(e.target.checked)}
                                    style={{ width: 'auto' }}
                                />
                                Show expired
                            </label>
                        </div>
                        {blacklistLoading ? (
                            <div className="loading-container"><div className="loading-spinner" /></div>
                        ) : blacklistEntries.filter(e => blShowExpired || !e.expiresAt || new Date(e.expiresAt) > new Date()).length === 0 ? (
                            <p className="no-data">{blShowExpired ? 'No blacklist entries found.' : 'No users are currently blacklisted.'}</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="table is-fullwidth is-striped">
                                    <thead>
                                        <tr>
                                            <th>Discord ID</th>
                                            <th className="mobile-hidden">Timeframe</th>
                                            <th className="mobile-hidden">Status</th>
                                            <th className="mobile-hidden">Added By</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blacklistEntries
                                            .filter(e => blShowExpired || !e.expiresAt || new Date(e.expiresAt) > new Date())
                                            .map(entry => {
                                                const isExpired = entry.expiresAt && new Date(entry.expiresAt) <= new Date();
                                                const isPermanent = !entry.expiresAt;
                                                return (
                                                    <tr key={entry.id} style={{ opacity: isExpired ? 0.5 : 1 }}>
                                                        <td><code style={{ fontSize: '0.85rem' }}>{entry.discordId}</code></td>
                                                        <td className="mobile-hidden">{entry.timeframe}</td>
                                                        <td className="mobile-hidden">
                                                            {isExpired ? (
                                                                <span className="status-pill" style={{ background: 'rgba(255,255,255,0.08)', color: '#aaa' }}>Expired</span>
                                                            ) : isPermanent ? (
                                                                <span className="status-pill" style={{ background: 'rgba(231,76,60,0.15)', color: '#ff9999' }}>Permanent</span>
                                                            ) : (
                                                                <span className="status-pill" style={{ background: 'rgba(46,204,113,0.15)', color: '#2ecc71' }}>Active</span>
                                                            )}
                                                        </td>
                                                        <td className="mobile-hidden">{entry.addedByName}</td>
                                                        <td>
                                                            <button
                                                                className="button is-small is-danger"
                                                                onClick={() => handleRemoveBlacklist(entry.id)}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Templates sub-tab ────────────────────────────────── */}
            {blacklistSubTab === 'templates' && (
                <div>
                    <div className="section">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <h3 style={{ margin: 0 }}>Message Templates ({blacklistTemplates.length})</h3>
                            <button className="button is-small is-primary" onClick={() => openTmplModal(null)}>
                                + New Template
                            </button>
                        </div>
                        <p style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '1rem' }}>
                            Templates let you reuse common blacklist messages. Each must contain <code>{'{timeframe}'}</code>, which is replaced with the timeframe you enter when blacklisting a user.
                        </p>
                        {blacklistTemplates.length === 0 ? (
                            <p className="no-data">No templates yet. Create one above.</p>
                        ) : (
                            <div className="blacklist-template-list">
                                {blacklistTemplates.map(tmpl => (
                                    <div key={tmpl.id} className="blacklist-template-card">
                                        <div className="blacklist-template-card-header">
                                            <span className="blacklist-template-name">{tmpl.name}</span>
                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                <button className="button is-small" onClick={() => openTmplModal(tmpl)}>Edit</button>
                                                <button className="button is-small is-danger" onClick={() => handleDeleteTemplate(tmpl.id)}>Delete</button>
                                            </div>
                                        </div>
                                        <p className="blacklist-template-message">{tmpl.message}</p>
                                        <p className="blacklist-template-id">ID: <code>{tmpl.id}</code></p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Template editor modal ─────────────────────────────── */}
            {tmplModalOpen && (
                <div className="appeal-modal-backdrop" onClick={closeTmplModal}>
                    <div className="appeal-modal" onClick={e => e.stopPropagation()}>
                        <div className="appeal-modal-header">
                            <h2 className="appeal-modal-title">
                                {tmplEditTarget ? 'Edit Template' : 'New Template'}
                            </h2>
                            <button className="appeal-modal-close" onClick={closeTmplModal} aria-label="Close">×</button>
                        </div>

                        <div className="field" style={{ marginBottom: '0.75rem' }}>
                            <label className="label">Friendly name <span style={{ color: 'var(--danger)' }}>*</span></label>
                            <input
                                type="text"
                                placeholder="e.g. Excessive Appeals"
                                value={tmplName}
                                onChange={e => setTmplName(e.target.value)}
                            />
                        </div>
                        <div className="field">
                            <label className="label">
                                Message <span style={{ color: 'var(--danger)' }}>*</span>
                                <span className="blacklist-tag-hint"> — must include <code>{'{timeframe}'}</code></span>
                            </label>
                            <textarea
                                placeholder={'e.g. Your appeal privileges have been suspended due to excessive appeals. You may not submit a new appeal until {timeframe}.'}
                                value={tmplMessage}
                                onChange={e => setTmplMessage(e.target.value)}
                                rows={4}
                            />
                        </div>

                        {tmplError && <p className="help is-danger" style={{ margin: '0.5rem 0' }}>{tmplError}</p>}

                        <div className="appeal-modal-actions">
                            <button className="button" onClick={closeTmplModal} disabled={tmplSubmitting}>Cancel</button>
                            <button className="button is-primary" onClick={handleSaveTemplate} disabled={tmplSubmitting}>
                                {tmplSubmitting ? 'Saving…' : tmplEditTarget ? 'Save Changes' : 'Create Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <section className="section">
            <h1>Appeals</h1>

            {/* ── Primary tabs ─────────────────────────────────────── */}
            <div className="blacklist-tabs">
                <button
                    className={`blacklist-tab${activeTab === 'appeals' ? ' blacklist-tab-active' : ''}`}
                    onClick={() => setActiveTab('appeals')}
                >
                    Appeals
                </button>
                <button
                    className={`blacklist-tab${activeTab === 'blacklist' ? ' blacklist-tab-active' : ''}`}
                    onClick={() => setActiveTab('blacklist')}
                >
                    Blacklist
                </button>
            </div>

            {/* ── Blacklist tab ─────────────────────────────────────── */}
            {activeTab === 'blacklist' && renderBlacklistTab()}

            {/* ── Appeals tab ──────────────────────────────────────── */}
            {activeTab === 'appeals' && (
                <div>
                    <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                        View and manage ban appeals submitted by users.
                    </p>

                    {error && (
                        <div className="error-message" style={{ marginBottom: '1rem' }}>
                            {error}
                        </div>
                    )}

                    <div className="appeals-filters">
                        <div className="appeals-filter-group">
                            <label className="appeals-filter-label">Username</label>
                            <input
                                type="text"
                                className="appeals-filter-input"
                                placeholder="Filter by username..."
                                value={filterUsername}
                                onChange={e => setFilterUsername(e.target.value)}
                            />
                        </div>

                        <div className="appeals-filter-group">
                            <label className="appeals-filter-label">Status</label>
                            <select
                                className="appeals-filter-input"
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value as 'all' | AppealStatus)}
                            >
                                <option value="all">All</option>
                                <option value="outstanding">Outstanding</option>
                                <option value="open">Open</option>
                                <option value="info_requested">Info Requested</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-container" style={{ marginTop: '1.5rem' }}>
                            <div className="loading-spinner" />
                            <p className="loading-text">Loading appeals...</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table is-fullwidth is-striped">
                                <thead>
                                    <tr>
                                        <th className="is-clickable" onClick={() => handleSortClick('username')}>
                                            Username{sortLabel('username')}
                                        </th>
                                        <th className="mobile-hidden">Ban date</th>
                                        <th className="mobile-hidden">Ban reason</th>
                                        <th className="is-clickable" onClick={() => handleSortClick('submittedAt')}>
                                            Appeal date/time{sortLabel('submittedAt')}
                                        </th>
                                        <th>Status</th>
                                        <th style={{ width: '240px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleAppeals.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: 'center', opacity: 0.7 }}>
                                                No appeals found.
                                            </td>
                                        </tr>
                                    ) : (
                                        visibleAppeals.map(appeal => (
                                            <tr key={appeal.id}>
                                                <td>{appeal.username}</td>
                                                <td className="mobile-hidden">{formatDate(appeal.banDate)}</td>
                                                <td className="mobile-hidden" style={{ maxWidth: 260 }}>
                                                    <span className="line-clamp-2">{appeal.banReason || 'N/A'}</span>
                                                </td>
                                                <td>{formatDateTime(appeal.submittedAt)}</td>
                                                <td>
                                                    <span className={`status-pill status-${appeal.status}`}>
                                                        {STATUS_LABELS[appeal.status] ?? appeal.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="appeals-actions">
                                                        <button
                                                            className="button is-small"
                                                            onClick={() => setSelectedAppeal(appeal)}
                                                        >
                                                            View
                                                        </button>
                                                        {(appeal.status === 'open' || appeal.status === 'info_requested') && (
                                                            <div className="status-actions">
                                                                <button
                                                                    className="button is-small is-success"
                                                                    onClick={() => { setSelectedAppeal(appeal); openResolutionModal(appeal, 'accepted'); }}
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="button is-small is-danger"
                                                                    onClick={() => { setSelectedAppeal(appeal); openResolutionModal(appeal, 'rejected'); }}
                                                                >
                                                                    Reject
                                                                </button>
                                                                <button
                                                                    className="button is-small is-info"
                                                                    onClick={() => { setSelectedAppeal(appeal); openRequestModal(appeal); }}
                                                                >
                                                                    Request Info
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Detail modal ───────────────────────────────────────────── */}
            {selectedAppeal && (
                <div className="appeal-modal-backdrop" onClick={() => setSelectedAppeal(null)}>
                    <div className="appeal-modal" onClick={e => e.stopPropagation()}>
                        <div className="appeal-modal-header">
                            <h2 className="appeal-modal-title">
                                Appeal from {selectedAppeal.username}
                            </h2>
                            <button
                                className="appeal-modal-close"
                                onClick={() => setSelectedAppeal(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <dl className="appeal-modal-grid">
                            <dt>Status:</dt>
                            <dd>
                                <span className={`status-pill status-${selectedAppeal.status}`}>
                                    {STATUS_LABELS[selectedAppeal.status] ?? selectedAppeal.status}
                                </span>
                            </dd>

                            <dt>Discord ID:</dt>
                            <dd>{selectedAppeal.discordId || 'N/A'}</dd>

                            <dt>Username:</dt>
                            <dd>{selectedAppeal.username}</dd>

                            <dt>Ban Date:</dt>
                            <dd>{formatDate(selectedAppeal.banDate)}</dd>

                            <dt>Ban Reason:</dt>
                            <dd>{selectedAppeal.banReason || 'N/A'}</dd>

                            <dt>Understanding/Objection:</dt>
                            <dd>{selectedAppeal.understandingObjection || 'N/A'}</dd>

                            <dt>If I get unbanned:</dt>
                            <dd>{selectedAppeal.ifUnbanned || 'N/A'}</dd>

                            <dt>Additional Comments:</dt>
                            <dd>{selectedAppeal.additionalComments || 'N/A'}</dd>

                            <dt>Resolved By:</dt>
                            <dd>{selectedAppeal.resolvedByName || '—'}</dd>

                            {selectedAppeal.status !== 'open' && selectedAppeal.status !== 'info_requested' && (
                                <>
                                    <dt>Resolution Comments:</dt>
                                    <dd>{selectedAppeal.resolutionComments || '—'}</dd>
                                </>
                            )}

                            <dt>Submitted Date/Time:</dt>
                            <dd>{formatDateTime(selectedAppeal.submittedAt)}</dd>
                        </dl>

                        {/* Request thread */}
                        {renderRequestThread(selectedAppeal)}

                        {/* Action buttons */}
                        {(selectedAppeal.status === 'open' || selectedAppeal.status === 'info_requested') && (
                            <div className="appeal-modal-actions">
                                <button
                                    className="button is-small is-success"
                                    onClick={() => openResolutionModal(selectedAppeal, 'accepted')}
                                >
                                    Approve
                                </button>
                                <button
                                    className="button is-small is-danger"
                                    onClick={() => openResolutionModal(selectedAppeal, 'rejected')}
                                >
                                    Reject
                                </button>
                                <button
                                    className="button is-small is-info"
                                    onClick={() => openRequestModal(selectedAppeal)}
                                >
                                    Request Info
                                </button>
                            </div>
                        )}

                        {selectedAppeal.status !== 'open' && selectedAppeal.status !== 'info_requested' && (
                            <div className="appeal-modal-actions">
                                <button
                                    className="button is-small is-warning"
                                    onClick={() => openOverrideModal(selectedAppeal)}
                                >
                                    Change Status
                                </button>
                            </div>
                        )}

                        {/* Resolution sub-modal */}
                        {resolutionTarget && (isOverride ? true : resolutionStatus) && (
                            <div className="appeal-modal-backdrop" onClick={closeResolutionModal}>
                                <div className="appeal-modal" onClick={e => e.stopPropagation()}>
                                    <div className="appeal-modal-header">
                                        <h2 className="appeal-modal-title">
                                            {isOverride
                                                ? `Change status for ${resolutionTarget.username}`
                                                : `${resolutionStatus === 'accepted' ? 'Approve' : 'Reject'} appeal for ${resolutionTarget.username}`}
                                        </h2>
                                        <button className="appeal-modal-close" onClick={closeResolutionModal} aria-label="Close">×</button>
                                    </div>

                                    {isOverride && (
                                        <div className="field" style={{ marginBottom: '1rem' }}>
                                            <label className="label">New status</label>
                                            <div className="control">
                                                <div className="select">
                                                    <select
                                                        value={overrideNewStatus}
                                                        onChange={e => setOverrideNewStatus(e.target.value as AppealStatus)}
                                                    >
                                                        <option value="open">Open</option>
                                                        <option value="accepted">Accepted</option>
                                                        <option value="rejected">Rejected</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <p className="help" style={{ marginTop: '0.25rem', opacity: 0.7 }}>
                                                Current status: <strong>{resolutionTarget.status}</strong>
                                            </p>
                                        </div>
                                    )}

                                    <div className="field">
                                        <label className="label">
                                            Resolution comments
                                            {(overrideNewStatus === 'open' || overrideNewStatus === 'info_requested') && isOverride
                                                ? ' (optional when re-opening)'
                                                : ' (required)'}
                                        </label>
                                        <div className="control">
                                            <textarea
                                                className="textarea"
                                                placeholder={
                                                    isOverride
                                                        ? 'Explain why this status is being changed...'
                                                        : resolutionStatus === 'accepted'
                                                            ? 'Explain why this appeal is being accepted...'
                                                            : 'Explain why this appeal is being rejected...'
                                                }
                                                value={resolutionComments}
                                                onChange={e => setResolutionComments(e.target.value)}
                                                rows={4}
                                            />
                                        </div>
                                        {resolutionError && (
                                            <p className="help is-danger" style={{ marginTop: '0.5rem' }}>
                                                {resolutionError}
                                            </p>
                                        )}
                                    </div>

                                    <div className="appeal-modal-actions">
                                        <button className="button" onClick={closeResolutionModal}>Cancel</button>
                                        <button
                                            className={`button ${isOverride ? 'is-warning' : resolutionStatus === 'accepted' ? 'is-success' : 'is-danger'}`}
                                            onClick={confirmResolution}
                                        >
                                            {isOverride
                                                ? `Set to ${STATUS_LABELS[overrideNewStatus] ?? overrideNewStatus}`
                                                : `Confirm ${resolutionStatus === 'accepted' ? 'Approval' : 'Rejection'}`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Request Info modal ─────────────────────────────────────── */}
            {requestTarget && (
                <div className="appeal-modal-backdrop" onClick={closeRequestModal}>
                    <div className="appeal-modal" onClick={e => e.stopPropagation()}>
                        <div className="appeal-modal-header">
                            <h2 className="appeal-modal-title">
                                Request Further Information — {requestTarget.username}
                            </h2>
                            <button className="appeal-modal-close" onClick={closeRequestModal} aria-label="Close">×</button>
                        </div>

                        <p style={{ opacity: 0.75, fontSize: '0.9rem', marginBottom: '1rem' }}>
                            The appealer will see your question on the appeal site and can submit a reply.
                            The appeal status will change to <strong>Info Requested</strong> until they respond.
                            Your identity will appear as <strong>MHA Staff</strong>.
                        </p>

                        {/* Existing thread (if any) */}
                        {(requestTarget.requestMessages ?? []).length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.4rem' }}>
                                    Previous messages in this thread:
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 200, overflowY: 'auto' }}>
                                    {(requestTarget.requestMessages ?? []).map(msg => (
                                        <MessageBubble key={msg.id} msg={msg} username={requestTarget.username} dimmed />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="field">
                            <label className="label">Your question / information request (required)</label>
                            <div className="control">
                                <textarea
                                    className="textarea"
                                    placeholder="e.g. Have you changed your account password since the incident?"
                                    value={requestQuestion}
                                    onChange={e => setRequestQuestion(e.target.value)}
                                    rows={4}
                                />
                            </div>
                            {requestError && (
                                <p className="help is-danger" style={{ marginTop: '0.5rem' }}>
                                    {requestError}
                                </p>
                            )}
                        </div>

                        <div className="appeal-modal-actions">
                            <button className="button" onClick={closeRequestModal} disabled={requestLoading}>
                                Cancel
                            </button>
                            <button
                                className="button is-info"
                                onClick={submitInfoRequest}
                                disabled={requestLoading}
                            >
                                {requestLoading ? 'Sending…' : 'Send Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};


// ── Message bubble sub-component ─────────────────────────────────────────────

interface MessageBubbleProps {
    msg: RequestMessage;
    username: string;
    dimmed?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ msg, username, dimmed = false }) => {
    const formatTime = (value: string) => {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleString('en-GB');
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.fromStaff ? 'flex-start' : 'flex-end',
                opacity: dimmed ? 0.6 : 1,
            }}
        >
            <div
                style={{
                    maxWidth: '85%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: msg.fromStaff ? '0 8px 8px 8px' : '8px 0 8px 8px',
                    background: msg.fromStaff
                        ? 'rgba(88, 101, 242, 0.18)'
                        : 'rgba(255, 255, 255, 0.08)',
                    border: msg.fromStaff
                        ? '1px solid rgba(88, 101, 242, 0.35)'
                        : '1px solid rgba(255,255,255,0.12)',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                }}
            >
                <div style={{ fontWeight: 600, fontSize: '0.78rem', marginBottom: '0.2rem', opacity: 0.8 }}>
                    {msg.fromStaff ? 'MHA Staff' : username}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.message}</div>
                <div style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: '0.25rem', textAlign: 'right' }}>
                    {formatTime(msg.createdAt)}
                </div>
            </div>
        </div>
    );
};

export default Appeals;